import { Box, Divider, Stack, SxProps, useTheme } from '@mui/material';
import { Button } from 'system/atoms';
import { useDebounce } from '@uidotdev/usehooks';
import { useSearchablePagination, useTableSort } from 'common/hooks';
import { SearchInput } from 'common/ui';
import { Header } from 'common/ui/Header';
import { RefreshButton } from 'components/Buttons';
import { TableV2 } from 'components/Table/TableV2';
import { Tabs } from 'components/Tabs';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useUser } from 'core/stores/hooks/useUser';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import {
  useInitializeCampaignWithParticipantsMutation,
  useSearchCampaignsQuery,
  useSearchCampaignsTotalCountQuery
} from 'generated-graphql/hooks/apollo-hooks';
import {
  Conversation,
  SearchableCampaignSortableFields,
  SearchableSortDirection
} from 'generated-graphql/types';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { TabsValue } from 'modules/campaigns/constants/tabs';
import {
  SearchCampaignItem,
  SearchCampaignItems
} from 'modules/campaigns/types/SearchCampaigns';
import { getCampaignsListTabs } from 'modules/campaigns/utils/getCampaignsListTabs';
import { getDefaultCampaignsQueryVariables } from 'modules/campaigns/utils/getDefaultCampaignsQueryVariables';
import { getPaginationTotalRowsByTabValue } from 'modules/campaigns/utils/getPaginationTotalRowsByTabValue';
import { getSelectCampaignsColumns } from 'modules/campaigns/utils/getSelectCampaignsColumns';
import { selectedParticipantsMapper } from 'modules/conversations/utils/selectedParticipantsMapper';
import { useCampaignsGroupHandler } from 'pages/Campaigns/hooks/useCampaignsGroupHandler';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Campaign } from 'services/campaign/types';
import { useDrawer } from 'services/drawer/hooks';
import { DrawerComponentType } from 'services/drawer/types';
import { useLoadingOverlay } from 'services/loading/hooks';
import { useConfirmationModal } from 'services/modals/hooks';
import { Paths } from 'services/navigation/paths';
import { ToastType, useToast } from 'services/toast';

const searchContainer: SxProps = {
  maxWidth: 320
};

type LocationState = {
  conversations: Conversation[];
  campaign: Campaign;
};

export const SelectExistingCampaign: React.FC = () => {
  const { isAdmin } = useIsAdmin();
  const organization = useOrganization();
  const user = useUser();
  const location = useLocation();

  const [campaignsList, setCampaignsList] = useState<SearchCampaignItems>([]);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState<string>('');
  const [existingCampaign, setExistingCampaign] =
    useState<SearchCampaignItem | null>(null);

  const debouncedSearch = useDebounce(search, 500);

  const [tabValue, setTabValue] = useState<TabsValue>(TabsValue.RECRUITMENT);
  const { showModal } = useConfirmationModal();
  const { showToast } = useToast();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const navigate = useNavigate();
  const { openDrawer } = useDrawer();
  const {
    palette: { customGrey }
  } = useTheme();

  const { handleSort, sort } = useTableSort<
    SearchableCampaignSortableFields,
    SearchCampaignItem
  >({
    field: SearchableCampaignSortableFields.CreatedAt,
    direction: SearchableSortDirection.Desc
  });

  const { conversations, campaign } = location.state as LocationState;

  const { from, handleNext, handlePrevious } = useSearchablePagination({
    limit
  });

  const defaultFilterValues = useMemo(() => {
    return {
      organizationId: organization?.id,
      userId: isAdmin ? undefined : user?.id,
      showLiveOnly: true
    };
  }, [organization?.id, isAdmin, user?.id]);

  const { loading, refetch } = useSearchCampaignsQuery({
    variables: {
      ...getDefaultCampaignsQueryVariables({
        ...defaultFilterValues,
        search: debouncedSearch,
        tabValue,
        sort
      }),
      from,
      limit
    },
    onCompleted: (result) => {
      setCampaignsList(extractQueryListItems(result));
    },
    notifyOnNetworkStatusChange: true
  });

  const { data: recruitmentCampaignsData } = useSearchCampaignsTotalCountQuery({
    variables: getDefaultCampaignsQueryVariables({
      ...defaultFilterValues,
      tabValue: TabsValue.RECRUITMENT
    })
  });

  const { data: otherCampaignData } = useSearchCampaignsTotalCountQuery({
    variables: getDefaultCampaignsQueryVariables({
      ...defaultFilterValues,
      tabValue: TabsValue.OTHER
    })
  });

  const {
    addChildrenCampaignsToParentGroupCampaign,
    removeChildrenCampaignsFromParentGroupCampaign
  } = useCampaignsGroupHandler({
    campaignsList,
    setCampaigns: setCampaignsList
  });

  const columns = getSelectCampaignsColumns({
    addChildrenCampaignsToParentGroupCampaign,
    removeChildrenCampaignsFromParentGroupCampaign,
    onJobTitleClick: (campaign) => {
      openDrawer({
        drawerContentType:
          DrawerComponentType.CampaignCreationJobDescriptionHtmlDrawer,
        drawerComponentProps: {
          jobDescriptionText: campaign?.jobDescriptionText || '',
          jobDescriptionParsed: campaign?.jobDescriptionParsed || {},
          hideEditButton: true
        }
      });
    }
  });

  const tabs = useMemo(() => {
    return getCampaignsListTabs({
      otherCount: otherCampaignData?.searchCampaigns?.total || 0,
      recruitmentCount: recruitmentCampaignsData?.searchCampaigns?.total || 0
    });
  }, [
    recruitmentCampaignsData?.searchCampaigns?.total,
    otherCampaignData?.searchCampaigns?.total
  ]);

  const paginationTotalRows = useMemo(
    () =>
      getPaginationTotalRowsByTabValue({
        tabValue,
        recruitmentCount: recruitmentCampaignsData?.searchCampaigns?.total || 0,
        otherCount: otherCampaignData?.searchCampaigns?.total || 0
      }),
    [
      tabValue,
      recruitmentCampaignsData?.searchCampaigns?.total,
      otherCampaignData?.searchCampaigns?.total
    ]
  );

  const [mutate] = useInitializeCampaignWithParticipantsMutation({
    onCompleted: (data) => {
      showToast({
        message: 'Message to candidates are sending',
        type: ToastType.Success
      });

      hideLoadingOverlay();

      if (data?.initializeCampaignWithParticipants?.campaignId) {
        const campaignId = data.initializeCampaignWithParticipants.campaignId;
        navigate(`${Paths.OUTBOUND_CAMPAIGN}/${campaignId}`);
      }
    },
    onError: () => {
      showToast({
        message: 'Error sending message to candidates',
        type: ToastType.Error
      });

      hideLoadingOverlay();
    }
  });

  return (
    <Stack p={4} spacing={4}>
      <Header
        title="Choose an existing campaign"
        currentBreadcrumb="choose existing campaign"
      />

      <Tabs
        tabs={tabs}
        onChange={(value) => {
          setTabValue(value);
        }}
        value={tabValue}
      />

      <Divider />

      <Stack direction="row" justifyContent="space-between">
        <Box sx={searchContainer}>
          <SearchInput
            placeholder="Search for a campaign"
            onChange={(value) => {
              setSearch(value);
            }}
          />
        </Box>

        <Stack direction="row" spacing={2}>
          <RefreshButton isLoading={loading} onClick={() => refetch()} />
          <Button
            variant="primary"
            onClick={() => {
              if (!existingCampaign?.id) {
                showModal({
                  title: 'Choose a campaign',
                  message: 'Choose a campaign.',
                  hideCancel: true,
                  confirmationText: 'OK'
                });
                return;
              }

              showModal({
                title: 'Add candidates to this campaign?',
                message: `You're about to add ${conversations.length} candidate(s) to the ${existingCampaign.title}.`,
                onConfirm: () => {
                  showLoadingOverlay();

                  mutate({
                    variables: {
                      input: {
                        parentCampaignId: campaign.id,
                        existingCampaignId: existingCampaign.id,
                        closeOngoingConversations: true,
                        selectedParticipants:
                          selectedParticipantsMapper(conversations)
                      }
                    }
                  });
                }
              });
            }}
          >
            Next
          </Button>
        </Stack>
      </Stack>

      <TableV2
        columns={columns}
        data={campaignsList}
        pagination
        selectableRowsSingle
        selectableRows
        isLoading={loading}
        sortField={sort.field}
        sortServer
        onSort={handleSort}
        selectableRowDisabled={(row) => row.id === campaign.id}
        paginationServer
        handleNext={handleNext}
        handlePrevious={handlePrevious}
        paginationTotalRows={paginationTotalRows}
        onChangeRowsPerPage={(rowsPerPage) => {
          setLimit(rowsPerPage);
        }}
        onSelectedRowsChange={({ selectedRows }) => {
          setExistingCampaign(selectedRows[0]);
        }}
        conditionalRowStyles={[
          {
            when: (row) => row.id === campaign.id,
            style: {
              backgroundColor: customGrey.light
            }
          }
        ]}
      />
    </Stack>
  );
};
